/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  ComponentConfig,
  ComponentState,
  PoeInterface,
  ReactPortletExport,
  SafConfig,
  WorkflowState
} from 'ehs-react';
import {
  ActionOutcome,
  createRequest,
  DataAccessPermission,
  ErrorBoundary,
  exponentialBackoff,
  HttpMethod,
  OrchestratableInstance,
  React,
  showError,
  WindowMode
} from 'ehs-react';
import { createRoot } from 'react-dom/client';

/**
 * Used to save the information that must be used in CCCaseStatePortlet.
 */
type NavigationInfo = {
  /**
   * The current workflow state.
   */
  workflowState: string;

  /**
   * Indicates if the WorkFlowState must be represented as a StateIcon in the CCCaseStatePortlet or not.
   */
  navigable: boolean;

  /**
   * The IconCls of the StateIcon.
   */
  iconCls: string;

  /**
   * Indicates if the State change, effects the next states or not.
   */
  changeable: boolean;
};

/**
 * The navigation outline for the whole workflow.
 */
type NavigationOutline = {
  /**
   * name (ID) of the workflow.
   */
  workflowID: string;

  /**
   * the info of each stage in the workflow.
   */
  navigationInfos: NavigationInfo[];
};

/**
 * The initial POE configuration bean.
 */
type PoeInitialConfigurationBean = {
  /**
   * the current COE workflow state.
   */
  state: WorkflowState;

  /**
   * A list of ordered component state ID's, that shall be informed.
   */
  orderedComponentStateIds: string[];

  /**
   * The workflow Navigation Outline.
   */
  navigationOutline: NavigationOutline;
  /**
   * The workflow definition ID.
   */
  workflowID: string;

  /** The ID of the current layout. */
  layoutId: number;

  /** The default state, applied to all components that will not be orchestrated. */
  defaultBehavior: ComponentState;
};

/**
 * The response bean retrieved from a state change AJAX call.
 */
type PoeStateResponseBean = {
  /**
   * the current COE workflow state.
   */
  state: WorkflowState;

  /**
   * A list of ordered component state ID's, that shall be informed.
   */
  orderedComponentStateIds: string[];

  /**
   * Data, that might have been updated during a COE call.
   */
  data?: unknown;
};

type CallbackObject = {
  /**
   * The function to call.
   */
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  fn: Function;
  /**
   * The scope under which the callback function should be invoked.
   */
  scope: any;
  /**
   * Additionally options that will be passed back to the caller.
   */
  eOpts: any;
};

/**
 * Holds the details needed for a component that has been registered on the page.
 */
type RegisteredComponentInfo = {
  /** The function that will be called upon state change. */
  callbackFct: (
    eventName: string,
    newComponentState: ComponentState,
    oldComponentState: ComponentState,
    data: any,
    eOpts: any
  ) => void;
  /** The scope under which the callback function will be called. */
  callbackScope: any;
  /**
   * Additionally options that will be passed to back to the caller.
   */
  eOpts: any;
  /**
   * Indicates if the registered component is orchestrated.
   */
  orchestrated: boolean;
  /**
   * The current state of the registered component.
   */
  state: ComponentState;
  /**
   * The ID of the portlet boundaries.
   */
  portletBoundaryId: string;
  /**
   * Custom div this portlet is rendered into, as configured in the workflow xml.
   */
  customDiv: string | undefined;
  /**
   * A map of listener context per listener name.
   */
  listeners: Record<string, CallbackObject>;
};

/**
 * The POE main class, that handles the client side portlet orchestration.
 */
export class POE implements PoeInterface {
  /**
   * The application url to perform poe state changes.
   */
  url: string;

  /**
   * The workflow definition ID.
   */
  workflowID: string;

  /**
   * The ID of the layout (page).
   */
  layoutId: number;

  /**
   * The workflow instance ID.
   */
  workflowInstanceID: string;

  /**
   * The current state of the workflow.
   */
  currentState: Record<string, ComponentState>;

  /**
   * The default component state in case the component
   *       cannot be orchestrated.
   */
  defaultComponentState: ComponentState;

  /**
   * map from component ID to a container, which holds
   *       information about the registered component.
   */
  registeredComponents: Record<string, RegisteredComponentInfo>;

  /**
   * The current workflow state name.
   */
  currentWorkflowState: string;

  /**
   * initial info for workflow.
   */
  navigationOutline: NavigationOutline;

  /**
   * The component configurations.
   */
  componentConfigs: Record<string, ComponentConfig>;

  /**
   * Constructor using fields.
   */
  constructor(url: string, initialConfig: PoeInitialConfigurationBean) {
    this.url = url;
    this.layoutId = initialConfig.layoutId;
    this.currentState = initialConfig.state.componentStates;
    this.workflowInstanceID = initialConfig.state.workflowInstanceID;
    this.currentWorkflowState = initialConfig.state.workflowState;
    this.navigationOutline = initialConfig.navigationOutline;
    this.workflowID = initialConfig.workflowID;
    this.defaultComponentState = initialConfig.defaultBehavior;
    this.registeredComponents = {};
    this.componentConfigs = initialConfig.state.componentConfigs;
    this.updateStore();
  }

  /**
   * Registers a certain component to the POE.
   * @param uniqueComponentID the component ID, which must be unique per page.
   * @param stateChangeCallback the state change callback function.
   * @param scope The scope under which the callback should be executed.
   * @param eOpts The options to pass back to the callback.
   * @return An OrchestrableInstance, which can be used to trigger events.
   */
  public registerComponent(
    uniqueComponentID: string,
    stateChangeCallback: (
      eventName: string,
      newComponentState: ComponentState,
      oldComponentState: ComponentState,
      data: any,
      eOpts: unknown
    ) => void,
    scope: unknown,
    eOpts: { portletId: string; customDiv: string | undefined }
  ): OrchestratableInstance {
    // validate unique component ID:
    if (uniqueComponentID == null) {
      throw new Error('Component ID not defined!');
    } else if (uniqueComponentID in this.registeredComponents) {
      // delete previously registered component in case the exactly same
      // component is registered again.
      // That is needed in case classic is loaded on demand and the classic
      // application is registered again.
      delete this.registeredComponents[uniqueComponentID];
    }

    // get corresponding component state:
    const orchestratedState: ComponentState = this.currentState[
      uniqueComponentID
    ];
    const orchestrated = !!orchestratedState;

    // check if orchestrated:
    if (!orchestrated) {
      // no orchestrated state found --> perform default handling:
      console.info(
        '%i: Component with ID="%s" will NOT be orchestrated!',
        new Date().getTime(),
        uniqueComponentID
      );
    } else {
      console.info(
        '%i: Component with ID="%s" will be orchestrated!',
        new Date().getTime(),
        uniqueComponentID
      );
    }

    // determine out liferay portlet container:
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const portletId: string = eOpts && eOpts.portletId;

    // no perform the registering:
    this.registeredComponents[uniqueComponentID] = {
      callbackFct: stateChangeCallback,
      callbackScope: scope,
      eOpts: eOpts,
      orchestrated: orchestrated,
      state: {
        active: false,
        windowMode: WindowMode.HIDDEN,
        dataAccessPermission: DataAccessPermission.NONE,
        stateChangingActions: []
      },
      portletBoundaryId: portletId,
      customDiv: eOpts.customDiv,
      listeners: {}
    };

    // build & return orchestrated instance:
    return new OrchestratableInstance(uniqueComponentID, orchestrated);
  }

  /**
   * Builds the application for a certain component.
   * @param uniqueComponentID the unique ID of the component.
   * @param appClass the application class as dot separated string. Format: PACKAGE.PACKAGE.APPCLASS
   * @param config the configuration to supply to the component.
   * @param jsFramework framework of the portlet to be loaded
   */
  buildApplication(
    uniqueComponentID: string,
    appClass: string,
    config: SafConfig<any>,
    jsFramework: string
  ): Promise<OrchestratableInstance> {
    // register component:

    // if this portlet has a specific div configured in the workflow xml, render it there.
    const customDiv = this.componentConfigs[uniqueComponentID]?.divId;
    if (customDiv) config.renderDiv = customDiv;

    return new Promise((resolve, reject) => {
      exponentialBackoff(
        () => document.getElementById(config.renderDiv) !== null,
        this.getBuildApplicationWhenDivRendered(
          this,
          uniqueComponentID,
          appClass,
          config,
          jsFramework,
          customDiv,
          { resolve, reject }
        ),
        `Unable to find div "${config.renderDiv}" for portlet with unique component instance id "${uniqueComponentID}"`
      );
    });
  }

  private getBuildApplicationWhenDivRendered(
    poe: this,
    uniqueComponentID: string,
    appClass: string,
    config: SafConfig<any>,
    jsFramework: string,
    customDiv: string | undefined,
    {
      resolve,
      reject
    }: { resolve: (value: OrchestratableInstance) => void; reject: () => void }
  ) {
    return () => {
      let application: any;
      // register component:
      const portletInstance: OrchestratableInstance = poe.registerComponent(
        uniqueComponentID,
        function (
          eventName: string,
          newComponentState: ComponentState,
          oldComponentState: ComponentState,
          data: any,
          eOpts: any
        ): void {
          try {
            console.info(
              new Date().getTime() +
                ': Callback of component with ID "' +
                uniqueComponentID +
                '" called...'
            );
            application.poeStateChanged(
              eventName,
              newComponentState,
              oldComponentState,
              data,
              eOpts
            );
          } catch (e) {
            console.error(
              "An error has occurred during calling poeStateChanged for component '" +
                uniqueComponentID +
                "'"
            );
            console.error(e);
          }
        },
        null,
        { portletId: config.portletId, customDiv: customDiv }
      );

      // get current component state:
      const currentCmpState: ComponentState =
        poe.currentState[uniqueComponentID] || poe.defaultComponentState;

      // make sure the class ends with '.Application' for ExtJS
      const extJsAppClass =
        appClass.indexOf('.Application') >= 0
          ? appClass
          : appClass + '.Application';
      // create application depending on type:
      if (jsFramework === 'ext-classic') {
        // that occurs only if the classic ExtJS has been loaded on demand by a React application:
        const extNs = 'Ext';
        const wd: any = window;
        const ExtJs: any = wd[extNs];
        application = ExtJs.create(
          extJsAppClass,
          ExtJs.applyIf(
            {
              portletInstance: portletInstance
            },
            config
          )
        );
      } else if (jsFramework === 'ext-modern') {
        const extNs = 'Ext6';
        const wd: any = window;
        const ExtJs: any = wd[extNs];

        // make sure the ext-modern viewport is initialized
        this.ensureModernViewportInitialized(ExtJs);

        application = ExtJs.create(
          extJsAppClass,
          ExtJs.applyIf(
            {
              portletInstance: portletInstance
            },
            config
          )
        );
      } else {
        // application is a normal react application:
        // --> split up the names to get the constructor:
        const appNameParts = appClass.split('.');
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        let portletPackage: ReactPortletExport = window;
        for (let i = 0; i < appNameParts.length; i++) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          portletPackage = portletPackage && portletPackage[appNameParts[i]];
        }
        // Log errors understandably
        if (
          !portletPackage ||
          !portletPackage.App ||
          !portletPackage.PortletStoreApi ||
          !portletPackage.poeStateChanged
        ) {
          console.error(
            `POE error: ${appClass} is not a valid React portlet: It does not export 'App', 'PortletStoreApi' and 'poeStateChanged'!`,
            portletPackage
          );
          reject();
          return;
        }

        // initialize the portlet store. This must happen before rendering the component!
        portletPackage.PortletStoreApi.setState({
          ...config,
          poeInstance: portletInstance
        });

        // render the application
        const reactEl = (
          <ErrorBoundary>
            <portletPackage.App />
          </ErrorBoundary>
        );
        // const reactEl = React.createElement(ErrorBoundary, undefined, [
        //   React.createElement(portletPackage.App)
        // ]);
        const renderDiv = document.getElementById(config.renderDiv);
        if (renderDiv == null) {
          throw new Error('Could not find render div: ' + config.renderDiv);
        }
        const root = createRoot(renderDiv);
        root.render(reactEl);

        // create the application callback:
        application = {
          poeStateChanged(
            eventName: string,
            newComponentState: ComponentState,
            oldComponentState: ComponentState,
            data: any,
            eOpts: any
          ): void {
            portletPackage.PortletStoreApi.setState({
              windowMode: newComponentState.windowMode,
              active: newComponentState.active
            });
            portletPackage
              .poeStateChanged(
                eventName,
                newComponentState,
                oldComponentState,
                data,
                eOpts
              )
              .catch(console.error);
          }
        };
      }

      // get corresponding component state & do notify:
      poe.doNotify(
        uniqueComponentID,
        currentCmpState,
        'init',
        poe.navigationOutline,
        uniqueComponentID
      );

      // update view:
      poe.updateStore();
      resolve(portletInstance);
    };
  }

  /**
   * Adds an event listener for a certain event name.
   * @param uniqueComponentID the ID of the registered component, which the event handler should be added for.
   * @param evtName the name of the event
   * @param fn the handler function
   * @param scope the handler scope
   * @param eOpts the options for the handler function.
   */
  public addEventListener(
    uniqueComponentID: string,
    evtName: string,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    fn: Function,
    scope: unknown,
    eOpts: unknown
  ): void {
    // lookup component info:
    const componentInfo:
      | RegisteredComponentInfo
      | undefined = this.lookupComponentInfo(uniqueComponentID);

    // check if available:
    if (componentInfo) {
      componentInfo.listeners[evtName] = {
        fn: fn,
        scope: scope || componentInfo,
        eOpts: eOpts
      };
    } else {
      console.warn(
        'Unable to add event listener "%s" as the component "%s" is not registered!',
        evtName,
        uniqueComponentID
      );
    }
  }

  /**
   * Removes an event listener from a registered component.
   * @param uniqueComponentID the unique ID of the component to remove the event listener for.
   * @param evtName the name of the event to delete.
   */
  public removeEventListener(uniqueComponentID: string, evtName: string): void {
    // lookup component info:
    const componentInfo:
      | RegisteredComponentInfo
      | undefined = this.lookupComponentInfo(uniqueComponentID);
    // check if available:
    if (componentInfo?.listeners?.[evtName]) {
      delete componentInfo.listeners[evtName];
    } else {
      console.warn(
        'Unable to remove event listener "%s" as the component "%s" is not registered!',
        evtName,
        uniqueComponentID
      );
    }
  }

  /**
   * Performs a certain action for a component, which might cause a POE state change.
   * @param uniqueComponentID the unique ID of the component that causes the action.
   * @param action the action to fire.
   * @param data the data for that action.
   * @return the ActionOutcome.
   */
  public performAction(
    uniqueComponentID: string,
    action: string,
    data: unknown
  ): ActionOutcome {
    // lookup component info:
    const componentInfo:
      | RegisteredComponentInfo
      | undefined = this.lookupComponentInfo(uniqueComponentID);

    // check if registered & orchestrated:
    if (!componentInfo) {
      // component not registered:
      console.warn(
        'Unable to trigger action "%s" as the component "%s" is not registered!',
        action,
        uniqueComponentID
      );
      return ActionOutcome.NOT_REGISTERED;
    } else if (!componentInfo.orchestrated) {
      console.info(
        'Unable to trigger POE.update for "%s" as the component "%s" is not orchestrated --> simply fire event',
        action,
        uniqueComponentID
      );
      // trigger Event firing:
      this.doNonPoeEventFire(action, data);
      return ActionOutcome.NOT_ORCHESTRATED;
    }
    // check whether a backend call is needed (otherwise simply fire Event
    const stateChangingActions = componentInfo.state.stateChangingActions;
    if (stateChangingActions?.includes(action)) {
      // we have to consult the POE backend:
      console.info(
        'POE has to be consulted for "%s" as the component "%s" is orchestrated and a state changing action.',
        action,
        uniqueComponentID
      );
      this.consultPoeForStateChange(uniqueComponentID, action, data);
    } else {
      console.info(
        'POE has not to be consulted for "%s" and the component "%s" as it is not a state changing action.',
        action,
        uniqueComponentID
      );
      // action has no influence on state change --> simply trigger event:
      this.doNonPoeEventFire(action, data);
    }
    return ActionOutcome.SUCCESS;
  }

  /**
   * Lookup the Component state for a certain component.
   * @param uniqueComponentID the unique ID of the component.
   * @return the current component state
   */
  public getState(uniqueComponentID: string): ComponentState {
    // lookup component info:
    const componentInfo:
      | RegisteredComponentInfo
      | undefined = this.lookupComponentInfo(uniqueComponentID);

    // check if component is registered:
    return componentInfo?.state ?? this.defaultComponentState;
  }

  /**
   * Fires an event to all registered components if they have a listener defined for that event name.
   * @param action the event name
   * @param data the event data.
   */
  private doNonPoeEventFire(action: string, data: any): void {
    // get all componentInfo's:
    const registeredComponentKeys = Object.keys(this.registeredComponents);
    console.debug(
      'Try to distribute event "%s" to #%i registered components',
      action,
      registeredComponentKeys.length
    );

    for (let i = 0; i < registeredComponentKeys.length; i++) {
      const key: string = registeredComponentKeys[i];
      const regCmp: RegisteredComponentInfo = this.registeredComponents[key];

      // extract callback object:
      const callbackObj: CallbackObject | null = regCmp.listeners[action];
      if (callbackObj !== null && callbackObj !== undefined) {
        console.debug(
          'Distribute event "%s" to component with key "%s" ...',
          action,
          key
        );

        setTimeout(() => {
          callbackObj.fn.apply(callbackObj.scope, [data, callbackObj.eOpts]);
        }, 0);
      }
    }
  }

  /**
   * Fire a non-statechanging event on a single component.
   *
   * This is only useful when delegating to another framework from a wrapper.
   *
   * @see PoeInterface#performAction
   *
   * @param uniqueComponentID
   * @param action
   * @param data
   */
  fireEventOnSingleComponent(
    uniqueComponentID: string,
    action: string,
    data: unknown
  ): void {
    console.debug(
      'refiring event %s on instance %s',
      action,
      uniqueComponentID
    );
    const handler = this.registeredComponents[uniqueComponentID]?.listeners[
      action
    ];

    if (handler === undefined) {
      console.error(
        'could not fire event %s for instance %s as it or the handler does not exist!',
        action,
        uniqueComponentID
      );
      return;
    }

    setTimeout(
      () => void handler.fn.apply(handler.scope, [data, handler.eOpts]),
      0
    );
  }

  /**
   * Notifies a single registered component about a state change.
   *
   * @param uniqueComponentID the ID of the registered component
   * @param newComponentState the new state
   * @param event the event.
   * @param data the data from the POE.
   * @param causingComponentID the ID of the notifying component
   */
  private doNotify(
    uniqueComponentID: string,
    newComponentState: ComponentState,
    event: string,
    data: unknown,
    causingComponentID: string
  ): void {
    // get info from registered portlet:
    const registeredComponentInfo: RegisteredComponentInfo = this
      .registeredComponents[uniqueComponentID];
    if (!registeredComponentInfo) {
      console.warn(
        'Component with ID="%s" not registered --> perform no update!',
        uniqueComponentID
      );
    } else {
      // get old component state:
      const oldComponentState: ComponentState = registeredComponentInfo.state;

      // store new state:
      registeredComponentInfo.state = newComponentState;

      // apply window mode to liferay boundary:
      POE.applyWindowModeToLiferayPortletBoundary(registeredComponentInfo);

      // assemble options:
      const options: any = JSON.parse(
        JSON.stringify(registeredComponentInfo.eOpts)
      );
      options.workflowState = this.currentWorkflowState;
      options.causingComponentID = causingComponentID;

      // perform callback:
      console.info(
        'Do callback on component with ID="%s" and event="%s"',
        uniqueComponentID,
        event
      );
      setTimeout(() => {
        // call callback in next event cycle:
        registeredComponentInfo.callbackFct.apply(
          registeredComponentInfo.callbackScope,
          [event, newComponentState, oldComponentState, data, options]
        );
      }, 0);
    }
  }

  /**
   * Helper method, that add the window mode to the portlet boundary if it is given.
   * @param registeredComponentInfo the RegisteredComponentInfo.
   */
  private static applyWindowModeToLiferayPortletBoundary(
    registeredComponentInfo: RegisteredComponentInfo
  ): void {
    // extract needed vars:
    const windowMode: WindowMode = registeredComponentInfo.state.windowMode;
    const portletBoundaryId: string | null =
      registeredComponentInfo.customDiv ||
      registeredComponentInfo.portletBoundaryId;

    // get element:
    const portletBoundaryEl: HTMLElement | null = document.getElementById(
      portletBoundaryId
    );
    if (portletBoundaryEl !== null) {
      // define css prefix:
      const portletWindowModePrefix = 'sense-windowmode-';

      // first remove the previous window mode classes:
      portletBoundaryEl.classList.remove(
        portletWindowModePrefix + WindowMode[WindowMode.HIDDEN]
      );
      portletBoundaryEl.classList.remove(
        portletWindowModePrefix + WindowMode[WindowMode.MAXIMIZED]
      );
      portletBoundaryEl.classList.remove(
        portletWindowModePrefix + WindowMode[WindowMode.MINIMIZED]
      );
      portletBoundaryEl.classList.remove(
        portletWindowModePrefix + WindowMode[WindowMode.NORMAL]
      );

      // now add the correct cls:
      portletBoundaryEl.classList.add(
        portletWindowModePrefix + WindowMode[windowMode]
      );
    }
  }

  /**
   * Helper method, which directly contacts the POE backend to get the newest state. This method will only be called if a state change will happen.
   * @param uniqueComponentID the ID of the component, which triggers the state change.
   * @param action the event name.
   * @param data the event data.
   */
  private consultPoeForStateChange(
    uniqueComponentID: string,
    action: string,
    data: any
  ): void {
    // perform AJAX call:
    createRequest<any, PoeStateResponseBean>({
      method: HttpMethod.POST,
      operation: 'performAction'
    })({
      url: this.url,
      layoutId: this.layoutId,
      workflowInstanceID: this.workflowInstanceID,
      orchestratedComponentID: uniqueComponentID,
      action: action,
      data: data
    })
      .then(decodedResponse => {
        // do success logic here ...
        const response: PoeStateResponseBean = decodedResponse.data;

        // assign new workflow state:
        this.currentWorkflowState = response.state.workflowState;

        // assign prioritized component states:
        this.currentState = response.state.componentStates;

        // trigger notify of all orchestrated states:
        for (const priority in response.orderedComponentStateIds) {
          const orchestratedComponentID =
            response.orderedComponentStateIds[priority];
          this.doNotify(
            orchestratedComponentID,
            this.currentState[orchestratedComponentID],
            action,
            data,
            uniqueComponentID
          );
        }

        // update info store:
        this.updateStore();

        // inform listeners that a state change has happened:
        // TODO: implement fireEvent logic to inform POE Admin View ... (only needed for POE Admin View)
      })
      .catch(showError);
  }

  /**
   * Helper method, which looks up the RegisteredComponentInfo for the passed ID,
   * @param uniqueComponentID the ID of the component to lookup.
   * @return the found RegisteredComponentInfo or null.
   */
  private lookupComponentInfo(
    uniqueComponentID: string
  ): RegisteredComponentInfo | undefined {
    // only lookup in the registered components...
    return this.registeredComponents[uniqueComponentID];
  }

  public getCompleteInfo(): void {
    // TODO: implement me!!!! (only needed for POE Admin View)
  }
  private updateStore(): void {
    // TODO: implement me!!!! (only needed for POE Admin View)
  }

  /**
   * Method to ensure that the modern ExtJS viewport is initialized.
   *
   * @param extNs the ExtJS namespace
   * @private
   */
  private ensureModernViewportInitialized(extNs: any): void {
    if (!extNs.Viewport) {
      // override Ext Body:
      extNs.getBody = function () {
        if (!extNs._senseBodyEl) {
          const liferayContentDiv = extNs.get('content');
          // create div where all the ExtJS stuff goes inside:
          extNs._senseBodyEl = liferayContentDiv.createChild(
            {
              cls: 'extjs-frame'
            },
            null,
            false
          );

          // do usual ExtJS stuff from original getBody function:
          extNs._senseBodyEl.skipGarbageCollection = true;
        }
        return extNs._senseBodyEl;
      };

      // config for the viewport
      const viewportCfg = {
        layout: {
          type: 'vbox',
          align: 'stretch'
        },
        flex: 1,
        cls: 'modern-all-area'
      };

      // setup viewport:
      extNs.viewport.Viewport.setup(viewportCfg);

      // invalidate setup as it must only be called once:
      extNs.viewport.Viewport.setup = extNs.emptyFn;

      // move main-content into ext-viewport
      const viewport = extNs.fly(
        'ext-viewport',
        // Ext.fly uses caller if no 2nd parameter is given, which is not allowed in strict mode,
        '_ensureModernViewportInitialized'
      );
      viewport.insertFirst('main-content');
    }
  }
}
